import { useMemo } from 'react';

import { LoadingSpinner } from '~/components/LoadingSpinner';

import { PreflightContext, type PreflightContextType } from './PreflightContext';
import { usePreflight } from './usePreflight';

type Props = {
  subscriberId: string;
  isLoggedIn: boolean;
  children: React.ReactNode;
};

/**
 * Owns the BE-resolved section-availability fetch for the v2 subscription-management surface and
 * exposes the four `has*` flags. Section availability is BE-resolved so we don't have to fetch every
 * domain payload just to count rows for the tab bar; only logged-in subscribers have tabs. Gates the
 * page on the visibility fetch (same pattern as the manage route) so the tab bar doesn't pop in after
 * first paint.
 */
export const PreflightProvider = ({ subscriberId, isLoggedIn, children }: Props) => {
  const { data: preflight, isLoading } = usePreflight({
    subscriberId,
    enabled: isLoggedIn && !!subscriberId,
  });

  const value = useMemo<PreflightContextType>(
    () => ({
      hasOrders: preflight?.has_orders ?? false,
      hasPodcasts: preflight?.has_podcasts ?? false,
      hasGroups: preflight?.has_groups ?? false,
      hasAdminBilling: preflight?.has_admin_billing ?? false,
    }),
    [preflight],
  );

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-dvh">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return <PreflightContext.Provider value={value}>{children}</PreflightContext.Provider>;
};
